/**
 * Hand-rolled fetch client.
 *
 * Reads `accessToken` and `tenant.id` from the Zustand auth store; attaches
 * `Authorization: Bearer ...` and `X-Tenant-Id` to every request. Throws a
 * typed `ApiError` on non-2xx responses so callers can pattern-match on
 * `error.code` instead of inspecting raw HTTP statuses.
 *
 * A future agent will replace this with an openapi-typescript generated
 * client; the surface of the helpers below (apiGet/Post/Patch/Delete) is
 * deliberately small so callers don't pin to fetch internals.
 */
import { useAuthStore } from './auth';
import { humanizeApiError } from './error-messages';

export interface ApiErrorBody {
  error?: {
    code?: string;
    type?: string;
    message?: string;
    retryable?: boolean;
    request_id?: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly requestId: string | undefined;
  public readonly details: unknown;

  constructor(status: number, body: ApiErrorBody | undefined, fallback: string) {
    const code = body?.error?.code ?? `HTTP_${status}`;
    const message = body?.error?.message ?? fallback;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryable = body?.error?.retryable ?? false;
    this.requestId = body?.error?.request_id;
    this.details = body?.error?.details;
  }
}

// Loosely-typed JSON payload. We don't pin Zod-derived types here because the
// caller may pass partials/optionals; the server validates against the same
// Zod schemas the caller used to build the body.
type Json = unknown;

export interface ApiRequestOptions {
  /** Optional override; default reads from Zustand store. */
  tenantId?: string | null;
  /** When true, do not send Authorization header (for /v1/auth/signup, /v1/auth/login). */
  unauthenticated?: boolean;
  /** Additional headers. */
  headers?: Record<string, string>;
  /** Set credentials mode; defaults to "include" so refresh cookies work. */
  credentials?: RequestCredentials;
  /** Abort signal forwarded to fetch. */
  signal?: AbortSignal;
}

function buildHeaders(opts: ApiRequestOptions, hasBody: boolean): Headers {
  const { accessToken, tenant } = useAuthStore.getState();
  const headers = new Headers(opts.headers ?? {});
  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');
  if (!opts.unauthenticated && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  const tenantId = opts.tenantId !== undefined ? opts.tenantId : tenant?.id;
  if (tenantId) {
    headers.set('X-Tenant-Id', tenantId);
  }
  return headers;
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    // Non-JSON response (e.g. 302 redirect URL).
    return text as unknown as T;
  }
}

async function request<T>(
  method: string,
  path: string,
  body: Json | undefined,
  opts: ApiRequestOptions,
  _retried = false,
): Promise<T> {
  const hasBody = body !== undefined;
  const init: RequestInit = {
    method,
    headers: buildHeaders(opts, hasBody),
    credentials: opts.credentials ?? 'include',
  };
  if (hasBody) {
    init.body = JSON.stringify(body);
  }
  if (opts.signal) {
    init.signal = opts.signal;
  }

  const res = await fetch(path, init);

  if (res.ok) return parse<T>(res);

  let errBody: ApiErrorBody | undefined;
  try {
    errBody = (await res.json()) as ApiErrorBody;
  } catch {
    errBody = undefined;
  }

  // Only treat 401 as a session problem. 403 (permission), 404, 409, 422, 5xx,
  // and the user-typed-the-wrong-password 401 from /v1/auth/login (which sets
  // `unauthenticated: true`) all surface as normal errors.
  if (res.status === 401 && !opts.unauthenticated && !_retried && !isRefreshPath(path)) {
    const ok = await tryRefresh();
    if (ok) {
      return request<T>(method, path, body, opts, true);
    }
    handleUnauthorized();
  }

  throw new ApiError(res.status, errBody, `${method} ${path} failed (${res.status})`);
}

function isRefreshPath(path: string): boolean {
  return path.startsWith('/v1/auth/refresh') || path.startsWith('/v1/auth/logout');
}

interface RefreshResponse {
  access_token: string;
  expires_in?: number;
  token_type?: string;
}

// Coalesce concurrent refresh attempts. If three queries all 401 at once we
// fire one /v1/auth/refresh and let the other two await the same promise.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch('/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const body = (await res.json()) as RefreshResponse;
      if (!body.access_token) return false;
      // Mutate the store directly — keep tenant/user/role; only the access
      // token rotates. (zustand's `set` is exposed via `setState`.)
      useAuthStore.setState({ accessToken: body.access_token });
      return true;
    } catch {
      return false;
    } finally {
      // Allow the next genuine 401 (e.g. user revoked) to retry refresh later.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

/**
 * Clear the auth store and bounce to /login when the access token can no longer
 * be refreshed (refresh cookie missing/revoked/expired). Skipped on /login and
 * /signup so a wrong password doesn't trigger a navigation loop.
 */
function handleUnauthorized(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path.startsWith('/login') || path.startsWith('/signup')) return;
  useAuthStore.getState().signOut();
  const next = encodeURIComponent(path + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

export function apiGet<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, opts);
}

export function apiPost<T>(
  path: string,
  body?: Json,
  opts: ApiRequestOptions = {},
): Promise<T> {
  return request<T>('POST', path, body, opts);
}

export function apiPatch<T>(
  path: string,
  body?: Json,
  opts: ApiRequestOptions = {},
): Promise<T> {
  return request<T>('PATCH', path, body, opts);
}

export function apiDelete<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  return request<T>('DELETE', path, undefined, opts);
}

/**
 * Extract per-field validation errors from an ApiError thrown by a VALIDATION_FAILED response.
 * The server returns details as `{ issues: { field_errors: { domain: ["..."] } }, target: 'body' }`;
 * this helper flattens to `{ domain: "...first error..." }` so forms can pin messages inline.
 *
 * Returns an empty object for any other error shape.
 */
export type FieldErrors = Record<string, string>;
export function extractFieldErrors(err: unknown): FieldErrors {
  if (!(err instanceof ApiError)) return {};
  const details = err.details as
    | { issues?: { field_errors?: Record<string, string[] | string> } }
    | undefined;
  const out: FieldErrors = {};
  const fe = details?.issues?.field_errors ?? {};
  for (const [k, v] of Object.entries(fe)) {
    if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string') out[k] = v[0];
    else if (typeof v === 'string') out[k] = v;
  }
  return out;
}

/**
 * Pull form-level (non-field-specific) validation errors. Returned as an array of strings.
 */
export function extractFormErrors(err: unknown): string[] {
  if (!(err instanceof ApiError)) return [];
  const details = err.details as
    | { issues?: { form_errors?: string[] } }
    | undefined;
  return details?.issues?.form_errors ?? [];
}

/**
 * Convert a snake_case API field name into a Title-Case label safe to show in a toast.
 * `project_id` → `Project`, `business_name` → `Business name`, `domain` → `Domain`.
 */
function prettyFieldLabel(field: string): string {
  const cleaned = field.replace(/_id$/i, '').replace(/_/g, ' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Build a toast-ready summary from any thrown value. Used by create / mutation
 * dialogs so the toast always shows the *actual* validation message instead of
 * the generic "Request validation failed" envelope.
 *
 *   const { title, description } = summarizeApiError(err, { fallbackTitle: "Could not save" });
 *   toast.error(title, { description });
 *
 * Resolution order for the description:
 *   1. All field-specific errors, one per line, formatted as `Field: message`.
 *   2. Form-level errors joined with newlines.
 *   3. The API error's `message` field.
 *   4. A generic fallback.
 */
export interface ApiErrorSummary {
  title: string;
  description: string;
  code: string | undefined;
}

/**
 * Build a `{ title, description, code }` summary suitable for a toast or
 * inline error message. Delegates all copywriting to `humanizeApiError` so
 * users see plain-English sentences instead of raw codes or stack traces.
 *
 * `opts.fallbackTitle` is respected only when the underlying error has no
 * recognised code — otherwise the humanised title (e.g. "Plan limit reached")
 * takes precedence because it is almost always more informative than the
 * caller's generic fallback ("Could not launch analysis").
 *
 * Field-level validation errors are still merged into the description via the
 * `humanizeApiError` code path; the `fieldLabels` override remains supported
 * for cases where the caller wants a nicer label than the auto-generated one.
 */
export function summarizeApiError(
  err: unknown,
  opts: { fallbackTitle?: string; fieldLabels?: Record<string, string> } = {},
): ApiErrorSummary {
  const human = humanizeApiError(err);

  const code = err instanceof ApiError ? err.code : undefined;

  // For VALIDATION_FAILED with caller-supplied field labels, rebuild the
  // description using those labels (nicer than the default prettyFieldLabel).
  if (err instanceof ApiError && (err.code === 'VALIDATION_FAILED' || err.status === 422)) {
    const fieldErrors = extractFieldErrors(err);
    const formErrors = extractFormErrors(err);
    const lines: string[] = [];
    for (const [field, message] of Object.entries(fieldErrors)) {
      const label = opts.fieldLabels?.[field] ?? prettyFieldLabel(field);
      lines.push(`${label}: ${message}`);
    }
    for (const m of formErrors) lines.push(m);
    if (lines.length > 0) {
      return {
        title: human.title,
        description: lines.join('\n'),
        code,
      };
    }
  }

  // For every other case, use the humanised copy verbatim — this is where
  // "AUTH_MISSING" becomes "Please sign in to continue." etc. If the humaniser
  // couldn't identify a specific error, prefer the caller's fallback title
  // (e.g. "Could not create project") over the generic "Something went wrong".
  const isGenericTitle = human.title === 'Something went wrong';
  const title =
    isGenericTitle && opts.fallbackTitle ? opts.fallbackTitle : human.title;
  return {
    title,
    description: human.description,
    code,
  };
}
